import React, { useState } from "react";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import { doc, setDoc, getDoc, getFirestore } from "firebase/firestore";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { setUser } from "stores/user/UserSlice";
import { User } from "interfaces/user";
import { motion } from "framer-motion";

// eslint-disable-next-line
import app from "firebaseConfig";
import { defaultStartingElo } from "constants/gameDefaults";

const auth = getAuth(app);

type LoginProps = {
  message: string;
};

function Login({ message }: LoginProps) {
  const dispatch = useDispatch();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [resending, setResending] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleUserProfile = async (user: any) => {
    const db = getFirestore();
    const userEloDoc = doc(db, "users", user.uid);
    const userEloSnapshot = await getDoc(userEloDoc);

    if (!userEloSnapshot.exists()) {
      const newUser: User = {
        uid: user.uid,
        currentElo: 1000,
        username: user.uid,
      };
      await setDoc(userEloDoc, {
        currentElo: defaultStartingElo,
        username: user.uid,
      });
      dispatch(setUser(newUser));
    } else {
      const userData = userEloSnapshot.data();
      const newUser: User = {
        uid: user.uid,
        currentElo: userData?.currentElo || defaultStartingElo,
        username: userData?.username || user.uid,
      };
      dispatch(setUser(newUser));
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await handleUserProfile(result.user);
    } catch (err) {
      console.error("Error signing in with Google: ", err);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let result;
      if (isSignUp) {
        result = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(result.user);
        setVerificationSent(true);
        setLoading(false);
        return;
      }
      result = await signInWithEmailAndPassword(auth, email, password);
      await handleUserProfile(result.user);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      const code = err?.code || "";
      if (code === "auth/email-already-in-use") {
        setError("This email is already registered. Try signing in.");
      } else if (code === "auth/invalid-email") {
        setError("Invalid email address.");
      } else if (code === "auth/weak-password") {
        setError("Password must be at least 6 characters.");
      } else if (
        code === "auth/user-not-found" ||
        code === "auth/wrong-password" ||
        code === "auth/invalid-credential"
      ) {
        setError("Invalid email or password.");
      } else {
        setError("An error occurred. Please try again.");
      }
      console.error("Email auth error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    try {
      const { currentUser } = auth;
      if (currentUser) {
        await sendEmailVerification(currentUser);
        setError("");
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err?.code === "auth/too-many-requests") {
        setError("Too many requests. Please wait a moment before retrying.");
      } else {
        setError("Failed to resend verification email.");
      }
    } finally {
      setResending(false);
    }
  };

  if (verificationSent) {
    return (
      <div className="Login" style={{ alignItems: "center" }}>
        <h3 className="connectPhrase" style={{ textAlign: "center" }}>
          Check your email
        </h3>
        <p style={{ textAlign: "center", opacity: 0.8, margin: "0.5rem 0" }}>
          A verification link has been sent to <strong>{email}</strong>.
          <br />
          Click the link, then sign in.
        </p>
        {error && <p className="emailError">{error}</p>}
        <motion.button
          className="connectButton"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            setVerificationSent(false);
            setIsSignUp(false);
          }}
        >
          Sign in
        </motion.button>
        <motion.button
          className="connectButton emailButton"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleResendVerification}
          disabled={resending}
        >
          {resending ? "Sending..." : "Resend verification email"}
        </motion.button>
      </div>
    );
  }

  return (
    <div className="Login">
      <h3 className="connectPhrase">{message}</h3>

      <motion.button
        className="connectButton"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleGoogleLogin}
      >
        <FontAwesomeIcon icon={faGoogle} />
        &nbsp; Sign in with Google
      </motion.button>

      {!showEmailForm ? (
        <motion.button
          className="connectButton emailButton"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowEmailForm(true)}
        >
          <FontAwesomeIcon icon={faEnvelope} />
          &nbsp; Sign in with Email
        </motion.button>
      ) : (
        <form className="emailForm" onSubmit={handleEmailSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="emailInput"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="emailInput"
            minLength={6}
            required
          />
          {error && <p className="emailError">{error}</p>}
          <motion.button
            type="submit"
            className="connectButton"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            disabled={loading}
          >
            {/* eslint-disable-next-line no-nested-ternary */}
            {loading ? "..." : isSignUp ? "Sign up" : "Sign in"}
          </motion.button>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
          <p
            className="emailToggle"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError("");
            }}
          >
            {isSignUp
              ? "Already have an account? Sign in"
              : "No account? Sign up"}
          </p>
        </form>
      )}
    </div>
  );
}

export default Login;
